import React, { useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { useNotification } from "../../hooks/useNotification";
import { bytes32ToString, shortAddress, formatTimestamp } from "../../utils/format";
import { PACKAGES } from "../../config/constants";
import Button from "../../components/Common/Button";
import { Card } from "../../components/Common/Card";

const PAGE_SIZE = 20;

function AdminUserList() {
  const notify = useNotification();
  const { getContract } = useWeb3();

  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false); // becomes true after first "Load"
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportFrom, setExportFrom] = useState("1");
  const [exportTo, setExportTo] = useState("");
  const cancelRef = useRef(false);

  // Total users
  useEffect(() => {
    (async () => {
      const contract = getContract();
      if (!contract) return;
      try {
        const t = await contract.totalUsers();
        const n = t.toNumber ? t.toNumber() : Number(t.toString());
        setTotalUsers(n);
        setExportTo(String(n)); // default to "1 to N"
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalUsers / PAGE_SIZE));

  // Load one page — only when `loaded` (user clicked "Load Users")
  useEffect(() => {
    if (!loaded) return;
    cancelRef.current = false;
    const load = async () => {
      const contract = getContract();
      if (!contract || totalUsers === 0) return;
      setLoading(true);
      try {
        // Serials are 1-indexed. Page 0 → serials 1..PAGE_SIZE
        const fromSerial = page * PAGE_SIZE + 1;
        const toSerial = Math.min(totalUsers, fromSerial + PAGE_SIZE - 1);
        const idsBn = await contract.getUsersBySerialRange(fromSerial, toSerial);
        const ids = idsBn.map((x) => x.toString()).filter((x) => x !== "0");

        const details = await Promise.all(
          ids.map(async (id) => {
            try {
              const u = await contract.users(id);
              // sponsor lookup
              let sponsorId = u.referrerId.toString();
              let sponsorName = "";
              if (sponsorId !== "0") {
                try {
                  const su = await contract.users(sponsorId);
                  sponsorName = bytes32ToString(su.name);
                } catch {}
              }
              // active package flags
              const pkgFlags = await Promise.all(
                PACKAGES.map(async (p) => {
                  try {
                    const raw = await contract.userPackages(id, p.id);
                    return raw.isActive;
                  } catch { return false; }
                })
              );
              return {
                id,
                serialNumber: u.serialNumber.toString(),
                name: bytes32ToString(u.name),
                wallet: u.wallet,
                sponsorId,
                sponsorName,
                joinTime: u.joinTime.toNumber(),
                isBlocked: u.isBlocked,
                packages: pkgFlags,
              };
            } catch { return null; }
          })
        );
        if (!cancelRef.current) setRows(details.filter(Boolean));
      } catch {
        // ignore
      } finally {
        if (!cancelRef.current) setLoading(false);
      }
    };
    load();
    return () => { cancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalUsers, loaded]);

  const exportCsv = async () => {
    const contract = getContract();
    if (!contract) return notify.error("Wallet not connected");
    if (totalUsers === 0) return notify.error("No users on this contract yet");

    // Parse + validate range
    const fromSerial = parseInt(exportFrom, 10);
    const toSerial = parseInt(exportTo, 10);
    if (!fromSerial || fromSerial < 1) return notify.error("Invalid start serial");
    if (!toSerial || toSerial < fromSerial) return notify.error("Invalid end serial");
    if (fromSerial > totalUsers) return notify.error(`Start beyond total users (${totalUsers})`);
    const clampedTo = Math.min(toSerial, totalUsers);
    const count = clampedTo - fromSerial + 1;
    if (!window.confirm(`Export serials ${fromSerial} to ${clampedTo} (${count} users)?`)) return;

    setExporting(true);
    setExportProgress(0);
    try {
      const chunkSize = 20;
      const allRows = [];
      let processed = 0;

      for (let start = fromSerial; start <= clampedTo; start += chunkSize) {
        const end = Math.min(clampedTo, start + chunkSize - 1);

        // Get user IDs for this chunk
        let idsBn;
        try {
          idsBn = await contract.getUsersBySerialRange(start, end);
        } catch (err) {
          notify.error(`Failed at serial ${start}: ${err.message?.slice(0, 60) || "RPC error"}`);
          setExporting(false);
          setExportProgress(0);
          return;
        }
        const ids = idsBn.map((x) => x.toString()).filter((x) => x !== "0");

        // Sequential per-user fetch (kinder on RPC than Promise.all storms)
        for (let i = 0; i < ids.length; i++) {
          const id = ids[i];
          try {
            const u = await contract.users(id);
            const pkgFlags = [];
            for (const p of PACKAGES) {
              try {
                const raw = await contract.userPackages(id, p.id);
                pkgFlags.push(raw.isActive ? "Y" : "N");
              } catch { pkgFlags.push("N"); }
            }
            allRows.push({
              serial: u.serialNumber.toString(),
              id,
              name: bytes32ToString(u.name).replace(/"/g, '""'),
              wallet: u.wallet,
              uplineId: u.referrerId.toString(),
              joinTime: formatTimestamp(u.joinTime.toNumber()),
              isBlocked: u.isBlocked ? "Y" : "N",
              packages: pkgFlags,
            });
          } catch { /* skip failed user */ }
          processed++;
          setExportProgress(Math.round((processed / count) * 100));
        }
      }

      if (allRows.length === 0) {
        notify.error("No users found in that range");
        setExporting(false);
        setExportProgress(0);
        return;
      }

      // Build CSV — BOM for Excel to detect UTF-8
      const header = [
        "Serial No", "User ID", "Name", "Wallet", "Upline ID", "Join Date (UTC)", "Blocked",
        ...PACKAGES.map((p) => `P${p.id} ($${p.price})`),
      ];
      const csvLines = [header.join(",")];
      for (const r of allRows) {
        csvLines.push([
          r.serial,
          r.id,
          `"${r.name}"`,
          r.wallet,
          r.uplineId,
          `"${r.joinTime}"`,
          r.isBlocked,
          ...r.packages,
        ].join(","));
      }
      const csvContent = "﻿" + csvLines.join("\r\n"); // BOM + Windows CRLF

      // Try Blob download first; fall back to data URI
      try {
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        a.href = url;
        a.download = `888global-users-${fromSerial}-${clampedTo}-${ts}.csv`;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch {
        // Fallback for browsers that block blob URL downloads
        const dataUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);
        window.open(dataUri, "_blank");
      }

      notify.success(`Exported ${allRows.length} users`);
    } catch (e) {
      notify.error("Export failed: " + (e.message?.slice(0, 80) || "unknown error"));
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  // Quick preset range buttons
  const setRange = (from, to) => {
    setExportFrom(String(from));
    setExportTo(String(Math.min(to, totalUsers)));
  };

  const copyAddr = async (addr) => {
    try {
      await navigator.clipboard.writeText(addr);
      notify.success("Address copied");
    } catch { notify.error("Copy failed"); }
  };

  return (
    <div>
      <div className="section-header" style={{ marginTop: 0 }}>
        <div className="section-title">All Users</div>
        <span className="badge badge-cyan">{totalUsers}</span>
      </div>

      {/* Export */}
      <Card className="mb-lg">
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
            Export Users to CSV
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            By serial number range · includes all package activation flags
          </div>
        </div>

        {/* Range inputs */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <div>
            <label className="input-label">From Serial</label>
            <input
              type="number"
              className="input"
              value={exportFrom}
              onChange={(e) => setExportFrom(e.target.value)}
              min="1"
              max={totalUsers || 1}
              disabled={exporting}
            />
          </div>
          <div style={{ paddingTop: 20, color: "var(--text-muted)", fontSize: 14 }}>→</div>
          <div>
            <label className="input-label">To Serial</label>
            <input
              type="number"
              className="input"
              value={exportTo}
              onChange={(e) => setExportTo(e.target.value)}
              min="1"
              max={totalUsers || 1}
              disabled={exporting}
            />
          </div>
        </div>

        {/* Quick presets */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          <PresetChip label="1-50" onClick={() => setRange(1, 50)} disabled={exporting} />
          <PresetChip label="1-100" onClick={() => setRange(1, 100)} disabled={exporting} />
          <PresetChip label="1-500" onClick={() => setRange(1, 500)} disabled={exporting} />
          <PresetChip label="1-1000" onClick={() => setRange(1, 1000)} disabled={exporting} />
          <PresetChip label={`All (${totalUsers})`} onClick={() => setRange(1, totalUsers)} disabled={exporting} />
        </div>

        <Button variant="primary" size="sm" full onClick={exportCsv} loading={exporting} disabled={!totalUsers}>
          {exporting ? `Exporting... ${exportProgress}%` : "⬇ Download CSV"}
        </Button>
      </Card>

      {/* Load button + section header */}
      {loaded && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Browsing {rows.length} user{rows.length !== 1 ? "s" : ""} on page {page + 1}
          </div>
          <Button variant="ghost" size="sm" onClick={() => { setLoaded(false); setRows([]); setPage(0); }}>
            Hide
          </Button>
        </div>
      )}

      {/* User rows */}
      {!loaded ? (
        <Card>
          <div style={{ textAlign: "center", padding: "8px 4px" }}>
            <div style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 600, marginBottom: 6 }}>
              Browse All Users
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
              Loads users in pages of {PAGE_SIZE} from the contract.<br/>
              Total on-chain: <b style={{ color: "var(--cyan)" }}>{totalUsers}</b>
            </div>
            <Button variant="primary" size="sm" onClick={() => setLoaded(true)} disabled={totalUsers === 0}>
              Load Users
            </Button>
          </div>
        </Card>
      ) : loading ? (
        <div className="empty-state">
          <span className="spinner spinner-lg" />
          <div className="empty-state-text mt-md">Loading users...</div>
        </div>
      ) : rows.length === 0 ? (
        <Card><div className="empty-state-text">No users on this page</div></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((r) => (
            <div key={r.id} style={{
              padding: 12,
              background: "#0a0e24",
              border: "1px solid var(--border-strong)",
              borderRadius: 12,
            }}>
              {/* Header row */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: "linear-gradient(135deg, var(--cyan), var(--violet))",
                  color: "white", fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                  border: "1px solid var(--cyan-border)",
                }}>
                  {r.name ? r.name[0]?.toUpperCase() : "◇"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
                      {r.name || "—"}
                    </span>
                    {r.isBlocked && <span className="badge badge-red" style={{ fontSize: 9 }}>BLOCKED</span>}
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--cyan)" }}>
                    #{r.id} · SN {r.serialNumber}
                  </div>
                </div>
              </div>

              {/* Wallet + copy */}
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 8px",
                background: "#050815",
                borderRadius: 6,
                marginBottom: 8,
              }}>
                <div style={{ flex: 1, fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.wallet}
                </div>
                <button
                  onClick={() => copyAddr(r.wallet)}
                  style={{
                    background: "var(--cyan)", color: "#000", border: "none",
                    padding: "3px 8px", borderRadius: 4, fontSize: 9,
                    fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-body)",
                  }}
                >
                  COPY
                </button>
              </div>

              {/* Meta */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8, fontSize: 11 }}>
                <div>
                  <div style={{ color: "var(--text-muted)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Upline</div>
                  <div style={{ color: "var(--text-secondary)" }}>
                    {r.sponsorName || "—"} <span style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>#{r.sponsorId}</span>
                  </div>
                </div>
                <div>
                  <div style={{ color: "var(--text-muted)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Joined</div>
                  <div style={{ color: "var(--text-secondary)", fontSize: 10 }}>{formatTimestamp(r.joinTime)}</div>
                </div>
              </div>

              {/* Package placement (P1-P8) */}
              <div>
                <div style={{ color: "var(--text-muted)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, marginBottom: 4 }}>Packages</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 3 }}>
                  {PACKAGES.map((p, i) => (
                    <div
                      key={p.id}
                      title={`P${p.id} ${r.packages[i] ? "Active" : "Locked"}`}
                      style={{
                        padding: "3px 0",
                        textAlign: "center",
                        fontSize: 10,
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        borderRadius: 4,
                        background: r.packages[i] ? "var(--cyan)" : "rgba(139,155,209,0.08)",
                        color: r.packages[i] ? "#000" : "var(--text-muted)",
                        border: r.packages[i] ? "none" : "1px solid var(--border)",
                      }}
                    >
                      P{p.id}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination — only when list is loaded */}
      {loaded && totalPages > 1 && (
        <div className="pagination" style={{ marginTop: 16 }}>
          <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading}>‹ Prev</Button>
          <span className="pagination-info">
            Page {page + 1} / {totalPages}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1 || loading}>Next ›</Button>
        </div>
      )}
    </div>
  );
}

function PresetChip({ label, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "5px 10px",
        borderRadius: 6,
        border: "1px solid var(--cyan-border)",
        background: "rgba(0, 229, 255, 0.06)",
        color: "var(--cyan)",
        fontSize: 11,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        fontFamily: "var(--font-body)",
      }}
    >
      {label}
    </button>
  );
}

export default AdminUserList;
